import type {
  CatalogEntry,
  CatalogField,
  DataDef,
  DataSourceDef,
  DataSourceFieldMap,
  TableNode,
} from '@safetyhub/shared';

/**
 * 카탈로그가 **선언한** 객체 필드의 자식 1건 — `path`는 부모 필드 기준 상대 경로(중첩 객체면 점 경로),
 * `label`은 카탈로그 라벨 그대로. 저작 표면이 `부모 › 자식` 후보를 만드는 재료다.
 */
export interface DeclaredChildField {
  path: string;
  label: string;
}

/**
 * 반복(over) 소스의 한 필드(칼럼) — 모달이 "이 칼럼을 어느 셀에 매핑" 후보로 제시한다.
 * nested=중첩 객체/배열 필드(평탄 자동매핑 제외, 고급 경로 입력 대상).
 */
export interface RepeatSourceField {
  key: string;
  label: string;
  /** 중첩(객체/배열) 필드면 true — 모달 자동 매핑에서 제외하고 고급 경로로 다룬다. */
  nested?: boolean;
  /**
   * 등록형 객체 필드의 위젯 키(`DataSourceFieldMap.widgetKey`) — 있으면
   * `REGISTERED_OBJECT_FIELD_CONTRACTS[widgetKey]`가 이 필드의 **자식 경로 계약**이다.
   * 본문 저작 표면(표시 키 피커·조건 키 후보)이 `위험도 › 빈도(가능성)` 같은 자식 후보를
   * 파생하는 근거라 여기까지 실어 나른다 — 떨구면 그 자식 경로는 다시 "직접 입력 몫"이 된다.
   * kind='dataSource'에서만 채워진다(dataDef/카탈로그 필드에는 이 축이 없다).
   */
  widgetKey?: string;
  /**
   * 카탈로그가 `returns[].fields`로 **선언한** 객체 필드의 자식(위 widgetKey 계약과는 다른 축 —
   * 계약은 위젯 레지스트리가, 이건 카탈로그 entry가 자식을 말한다). 위험성평가 결과서의
   * `riskTypeFlags`(13칸 boolean 객체)가 실전이다: 카탈로그는 `유형 ✓ — 추락` 같은 자식 13개를
   * 선언하는데 종전 변환이 `nested:true`만 남기고 자식을 버려 작성폼 행 필드 select·본문 키 피커·
   * 조건 키 후보 어디에도 0건이었다(2026-09-14 은닉 감사 D-3 — widgetKey 없는 객체 필드는 저작
   * 표면에서 처음부터 만들 수 없었다). rc bare 읽기가 `getByPath`라 `riskTypeFlags.fall` 점 경로가
   * 실제로 성립한다.
   * kind='catalog'/'catalogField'는 returns에서 직접, kind='dataSource'는 `from`(primary.<path> /
   * <lookup alias>.<path>)을 카탈로그로 해소해 채운다. dataDef에는 이 축이 없다.
   */
  children?: DeclaredChildField[];
}

/**
 * 카탈로그 객체 필드의 선언 자식을 평탄 경로 목록으로 — `type:'object'`이고 `fields`가 있을 때만.
 * 자식이 다시 객체면 점 경로로 한 단계씩 더 판다(`getByPath`가 그대로 닿는다). **배열 자식은 내지
 * 않는다** — 그 안은 항목 스코프라 부모 행의 점 경로로는 값이 없다(`parentRowArrayFieldOptions`가
 * 같은 이유로 최상위 배열만 후보로 낸다).
 */
export function declaredChildFields(f: CatalogField): DeclaredChildField[] {
  if (f.type !== 'object' || !f.fields?.length) return [];
  const out: DeclaredChildField[] = [];
  for (const child of f.fields) {
    if (child.type === 'array') continue;
    if (child.type === 'object' && child.fields?.length) {
      for (const grand of declaredChildFields(child)) {
        out.push({ path: `${child.path}.${grand.path}`, label: `${child.label} › ${grand.label}` });
      }
      continue;
    }
    out.push({ path: child.path, label: child.label });
  }
  return out;
}

/** entry.returns에서 점 경로(`a.b`)로 필드를 찾는다 — 각 세그먼트를 `fields`로 한 단계씩 내려간다. */
function findCatalogFieldByPath(
  entry: CatalogEntry | undefined,
  path: string,
): CatalogField | undefined {
  if (!entry) return undefined;
  const segs = path.split('.');
  let fields: CatalogField[] | undefined = entry.returns;
  let found: CatalogField | undefined;
  for (const seg of segs) {
    found = fields?.find((f) => f.path === seg);
    if (!found) return undefined;
    fields = found.fields;
  }
  return found;
}

/**
 * 조립 데이터 소스 필드(`DataSourceFieldMap`)의 **카탈로그 선언 자식** — `from`이 `primary.<path>`면
 * def.primary entry, `<alias>.<path>`면 그 lookup의 entry에서 해당 returns 필드를 찾아
 * `declaredChildFields`를 낸다. 상수(`{const}`)·manual def·미해소 경로는 빈 배열.
 *
 * `collectRepeatSources`(본문 표면)와 `repeatRowBindingOptions`(작성폼 행 필드 select)가 **같은
 * 함수**로 자식을 파생한다 — 사본으로 갈라지면 두 표면의 후보가 조용히 갈린다(contractChildOptions
 * 를 rowScopeKeys 한 곳에 둔 것과 같은 이유).
 */
export function declaredChildrenForDataSourceField(
  def: Pick<DataSourceDef, 'kind' | 'primary' | 'lookups'>,
  field: Pick<DataSourceFieldMap, 'from'>,
  catalog: CatalogEntry[] | undefined,
): DeclaredChildField[] {
  if (def.kind !== 'assembled' || typeof field.from !== 'string' || !catalog?.length) return [];
  const dot = field.from.indexOf('.');
  if (dot <= 0) return [];
  const alias = field.from.slice(0, dot);
  const path = field.from.slice(dot + 1);
  const entryKey =
    alias === 'primary'
      ? def.primary
      : (def.lookups ?? []).find((l) => l.alias === alias)?.source;
  if (!entryKey) return [];
  const catalogField = findCatalogFieldByPath(
    catalog.find((e) => e.key === entryKey),
    path,
  );
  return catalogField ? declaredChildFields(catalogField) : [];
}

/**
 * 반복(over) 소스 통일 추상 — 내장 데이터셋(DataDef)·카탈로그 배열(cardinality='list')·
 * 조립형 데이터 소스(dataSources, cardinality='list')·
 * 카탈로그 단건 entry의 배열 필드(returns type='array')를 단일 형태로 묶어
 * 모달이 소스 종류 무관하게 소비한다.
 * - kind='dataDef':      필드 식별자 = DataDefField.key
 * - kind='catalog':      필드 식별자 = CatalogField.path (entry.returns의 top-level)
 * - kind='dataSource':   필드 식별자 = DataSourceFieldMap.field (조립 결과 필드 = 정본)
 * - kind='catalogField': 단건 entry의 배열 필드(예: 담당자 서명 › 기여자 목록) — repeat 노드가
 *                        자체 binding(catalogKey=entryKey, select=fieldPath)으로 배열을 적재해
 *                        소비한다. 표 반복영역은 이 유형 미지원(소비 측에서 kind로 제외).
 */
export interface RepeatSource {
  key: string;
  label: string;
  kind: 'dataDef' | 'catalog' | 'dataSource' | 'catalogField';
  fields: RepeatSourceField[];
  /**
   * true면 반복영역 직접 소스 목록에서 숨겨야 하는 seed 전용 카탈로그 소스(entry.seedOnly 전파).
   * 목록에서 제외하지 않음 — 소비 측(반복영역 소스)에서만 filter(!seedOnly)로 걸러낸다.
   */
  seedOnly?: true;
  /** kind='catalogField' 전용 — 배열 필드를 보유한 카탈로그 entry key(binding.catalogKey). */
  entryKey?: string;
  /** kind='catalogField' 전용 — entry.returns 안 배열 필드 경로(binding.select). */
  fieldPath?: string;
}

/**
 * 조립(compute) 데이터셋의 파생 칼럼 한 개 — 어디서 온 칼럼인지(origin)를 실어
 * 같은 키가 여러 출처에 걸릴 때의 우선순위(dedup)에 쓴다.
 * (2026-07-26 접두 자동 노출 개편으로 편집기 구획 표시는 철거 — origin은 파생 내부 용도만 남음.)
 */
export interface DerivedDataDefField extends RepeatSourceField {
  /**
   * - 'inherited': 입력 소스(base/source/left+right)에서 값이 그대로 전달되는 칼럼
   * - 'computed' : assembler가 만들어 내는 출력 키(mergeFields/leftFields/rightFields)
   * - 'declared' : def.fields에 손으로 적힌 선언(라벨 override·예외용)
   */
  origin: 'inherited' | 'computed' | 'declared';
  /**
   * 상속 출처 소스의 표시명(카드에 "○○에서 상속"으로 병기).
   * 자체 선언이 상속 칼럼과 같은 키를 덮은 경우에도 남겨 둔다 — "지워도 값은 계속 들어온다"는
   * 안내(중복 선언 정리)의 근거가 된다.
   */
  fromLabel?: string;
}

/**
 * 부모 반복 소스의 **배열(중첩) 행 필드** 후보 — 본문 표 반복영역·repeat 노드가 부모 반복 **안**에
 * 있을 때의 over 후보(작성폼 `repeatOverCandidates.parentRowFields`의 본문 대응물, 2026-09-05).
 *
 * 엔진 근거: 반복 단위 렌더는 `mergeRepeatScope(data, item)` 스코프라, 안쪽 반복의 over가 부모
 * **행 객체의 배열 필드**면 그 행의 배열을 순회한다(TBM 세션 부 참석자 그리드 `attendeeTriples`가
 * 실전 — 템플릿 계약 spec "over는 소스 key 또는 행 필드명"). 종전 두 패널은 최상위 소스 목록만
 * 후보로 파생해 이 정상 참조가 "(알 수 없음)"/미선택으로 보였다.
 *
 * 배열 판정은 부모의 **primary 카탈로그 returns 타입**이 정본이다(dataSource 필드 선언에는 배열
 * 표식이 없다). 한 단계만 판다 — 부모 반복의 over 자체가 행 필드(2중 중첩)면 부모 소스 해소가
 * 안 되므로 빈 후보(종전 표기 유지)다.
 *
 * ⚠️ 후보는 **최상위 배열 필드만**이다(2026-09-14). rc의 over 해소는 평탄 조회
 * (`RenderTemplate` repeat `data[node.over]` · 반복영역 `data[region.over]`)라 하위 `fields`를 재귀해
 * 만든 점 경로(`a.b`)는 고를 수는 있어도 항상 0행이다 — 고를 수 없는 후보를 내지 않는다.
 */
export function parentRowArrayFieldOptions(
  parent: RepeatSource | undefined,
  catalog: CatalogEntry[],
  dataSources: DataSourceDef[] | undefined,
): { key: string; label: string }[] {
  if (!parent) return [];
  /** entry.returns의 **최상위** 배열 필드 경로 → label 맵(하위 fields는 걷지 않는다 — 위 ⚠️). */
  const arrayPaths = (entry: CatalogEntry | undefined): Map<string, string> => {
    const out = new Map<string, string>();
    for (const f of entry?.returns ?? []) {
      if (f.type === 'array') out.set(f.path, f.label);
    }
    return out;
  };
  if (parent.kind === 'catalog') {
    const paths = arrayPaths(catalog.find((e) => e.key === parent.key));
    return [...paths.entries()].map(([path, label]) => ({
      key: path,
      label: `${parent.label} › ${label}`,
    }));
  }
  if (parent.kind === 'dataSource') {
    const def = (dataSources ?? []).find((d) => d.key === parent.key);
    if (!def || def.kind !== 'assembled') return [];
    const paths = arrayPaths(catalog.find((e) => e.key === def.primary));
    const out: { key: string; label: string }[] = [];
    for (const f of def.fields) {
      if (typeof f.from !== 'string' || !f.from.startsWith('primary.')) continue;
      if (!paths.has(f.from.slice('primary.'.length))) continue;
      out.push({ key: f.field, label: `${parent.label} › ${f.label ?? f.field}` });
    }
    return out;
  }
  // dataDef(칼럼이 평탄 위젯 축)·catalogField(이미 배열 그 자체)는 중첩 배열 필드 후보가 없다.
  return [];
}

/** 파생 결과 — 유효 칼럼 목록 + 해소하지 못한 입력 소스 키(편집기 경고 표식). */
export interface DerivedDataDefFields {
  fields: DerivedDataDefField[];
  /** 입력에 적혔지만 소스 목록에서 못 찾은 키(오타·삭제된 소스). 중복 제거·선언 순서. */
  unresolved: string[];
}

/** origin 우선순위 — 같은 키가 여러 출처에 걸리면 높은 쪽이 이긴다(자체 선언이 최우선). */
const ORIGIN_RANK: Record<DerivedDataDefField['origin'], number> = {
  inherited: 0,
  computed: 1,
  declared: 2,
};

/** compute.inputs[name]을 소스 키로 읽는다(문자열·공백 아님일 때만). */
function readComputeInput(def: DataDef, name: string): string | undefined {
  const v = def.compute?.inputs?.[name];
  if (typeof v !== 'string') return undefined;
  const t = v.trim();
  return t || undefined;
}

/**
 * compute.options[name]을 field-map(Record<출력키, dotted-path>)으로 읽는다.
 * 존재 자체가 엔진 분기 조건이라(zip의 hasFieldMap), 빈 객체도 undefined가 아닌 `{}`로 돌려준다.
 */
function readFieldMap(def: DataDef, name: string): Record<string, string> | undefined {
  const v = def.compute?.options?.[name];
  if (!v || typeof v !== 'object' || Array.isArray(v)) return undefined;
  const out: Record<string, string> = {};
  for (const [k, path] of Object.entries(v as Record<string, unknown>)) {
    if (typeof path === 'string') out[k] = path;
  }
  return out;
}

/**
 * 조립(compute) 내장 데이터셋의 **유효 칼럼**을 파생한다 — base 칼럼 반복 선언을 없애기 위한 정본.
 *
 * 엔진(packages/report-composer/src/assemblers.ts) 기준으로 값은 이미 자동 전달된다:
 *   - join-by-key : base 행 전체 spread + 조인 소스 전 칼럼을 `${inputs.join}.${칼럼}` 접두
 *                   flat 키로 자동 산출(2026-07-26) + 레거시 mergeFields 출력 키
 *   - slice-range : source 행을 그대로 잘라 냄                → source 칼럼
 *   - zip-two-sources : field-map이 있으면 그 출력 키만, 없으면 left/right 전체 spread
 * 즉 def.fields 선언은 **에디터 표면(칼럼 목록·셀 바인딩 후보)에만** 쓰이는 표시 계약이라,
 * 입력 소스에서 상속 가능한 칼럼을 손으로 반복 선언할 이유가 없다.
 *
 * 우선순위(같은 키): 자체 선언(라벨 override) > 조립 출력 > 상속. 자리(순서)는 처음 나타난 곳을 유지.
 * 입력 소스가 또 다른 조립 데이터셋이면 **재귀 파생**하고, 방문 키 집합으로 순환을 끊는다.
 * compute가 없거나 미등록 assembler면 자체 선언만 돌려준다(엔진이 조립하지 않으므로 상속도 없음).
 *
 * @param sources collectRepeatSources 산출(내장 데이터셋/카탈로그 배열/조립 데이터 소스) — 입력 키 해소용
 * @param dataDefs 재귀 파생 대상(입력 소스가 조립 dataDef인 경우). 미전달이면 1단계만 파생.
 * @param visited 재귀 순환 가드(내부용) — 이미 파생 중인 dataDef 키 집합
 */
export function deriveComputeDataDefFields(
  def: DataDef,
  sources: RepeatSource[],
  dataDefs?: DataDef[],
  visited?: ReadonlySet<string>,
): DerivedDataDefFields {
  const acc = new Map<string, DerivedDataDefField>();
  const unresolved: string[] = [];
  const addUnresolved = (key: string) => {
    if (!unresolved.includes(key)) unresolved.push(key);
  };

  // 같은 키가 여러 출처에 걸리면 순서는 처음 자리를 지키고 내용만 높은 origin으로 승격한다.
  const put = (field: DerivedDataDefField) => {
    const key = field.key.trim();
    if (!key) return;
    const prev = acc.get(key);
    if (!prev) {
      acc.set(key, { ...field, key });
      return;
    }
    if (ORIGIN_RANK[field.origin] < ORIGIN_RANK[prev.origin]) return;
    acc.set(key, {
      ...field,
      key,
      // 라벨이 비면 앞선 출처의 라벨을 유지(빈 라벨로 좋은 상속 라벨을 지우지 않는다).
      label: field.label.trim() ? field.label : prev.label,
      // nested·widgetKey·children·상속 출처는 상속 정보라 승격 후에도 보존한다(자체 선언은 라벨만 덮는다).
      nested: field.nested ?? prev.nested,
      widgetKey: field.widgetKey ?? prev.widgetKey,
      children: field.children ?? prev.children,
      fromLabel: field.fromLabel ?? prev.fromLabel,
    });
  };

  const compute = def.compute;
  if (compute) {
    const seen = new Set(visited ?? []);
    seen.add(def.key);

    /** 입력 소스 키를 {표시명, 칼럼}으로 해소(조립 dataDef면 재귀 파생). 미해소면 unresolved 기록. */
    const resolveInput = (
      inputName: string,
    ): { label: string; fields: RepeatSourceField[] } | undefined => {
      const key = readComputeInput(def, inputName);
      if (!key) return undefined; // 미지정 — "아직 안 고름"은 경고 대상이 아니다.
      const src = sources.find((s) => s.key === key);
      if (!src) {
        addUnresolved(key);
        return undefined;
      }
      if (src.kind === 'dataDef' && !seen.has(key)) {
        const nestedDef = (dataDefs ?? []).find((d) => d.key === key);
        if (nestedDef?.compute) {
          const sub = deriveComputeDataDefFields(nestedDef, sources, dataDefs, seen);
          sub.unresolved.forEach(addUnresolved);
          return { label: src.label, fields: sub.fields };
        }
      }
      return { label: src.label, fields: src.fields };
    };

    /** 입력 소스 칼럼을 그대로 상속 칼럼으로 싣는다(엔진이 행을 spread하는 축). */
    const inheritFrom = (inputName: string) => {
      const resolved = resolveInput(inputName);
      if (!resolved) return;
      for (const f of resolved.fields) {
        put({ ...f, origin: 'inherited', fromLabel: resolved.label });
      }
    };

    /** field-map의 출력 키를 조립 출력 칼럼으로 싣는다(라벨은 소스 필드 라벨). */
    const outputsFrom = (mapName: string, inputName: string) => {
      const map = readFieldMap(def, mapName);
      if (!map) return;
      const resolved = resolveInput(inputName);
      for (const [outKey, srcPath] of Object.entries(map)) {
        const label = resolved?.fields.find((f) => f.key === srcPath)?.label ?? outKey;
        put({ key: outKey, label, origin: 'computed' });
      }
    };

    switch (compute.assembler) {
      case 'join-by-key': {
        // base 행 전체 보존(엔진 spread) — 비접두 상속 칼럼.
        inheritFrom('base');
        // 접두 자동 노출(2026-07-26): 엔진이 조인 소스의 모든 칼럼을 `${inputs.join}.${칼럼}`
        // flat 키로 base 행에 싣는다 — 전량을 바인딩 후보로 노출한다. 라벨은 출처 병기.
        const joinPath = readComputeInput(def, 'join');
        const joinResolved = resolveInput('join');
        if (joinPath && joinResolved) {
          for (const f of joinResolved.fields) {
            put({
              key: `${joinPath}.${f.key}`,
              label: `${joinResolved.label} · ${f.label || f.key}`,
              ...(f.nested ? { nested: true as const } : {}),
              ...(f.children ? { children: f.children } : {}),
              origin: 'inherited',
              fromLabel: joinResolved.label,
            });
          }
        }
        // 레거시 mergeFields 출력 키 — 기존 바인딩 호환(엔진이 계속 소비).
        outputsFrom('mergeFields', 'join');
        break;
      }
      case 'slice-range':
        // source를 자르기만 하므로 칼럼은 소스와 동일.
        inheritFrom('source');
        break;
      case 'zip-two-sources': {
        // field-map이 하나라도 있으면 출력 키만 나온다(엔진 hasFieldMap 분기 — spread 미실행).
        const left = readFieldMap(def, 'leftFields');
        const right = readFieldMap(def, 'rightFields');
        if (left || right) {
          if (left) outputsFrom('leftFields', 'left');
          if (right) outputsFrom('rightFields', 'right');
        } else {
          inheritFrom('left');
          inheritFrom('right');
        }
        break;
      }
      default:
        // 미등록 assembler — 엔진이 조립하지 않으므로(runAssembler null) 상속·출력 모두 없다.
        break;
    }
  }

  for (const f of def.fields) put({ key: f.key, label: f.label, origin: 'declared' });

  return { fields: [...acc.values()], unresolved };
}

/**
 * CatalogField 한 개를 RepeatSourceField로 — 중첩(object/array/하위 fields)이면 nested 표식.
 * 객체 필드의 선언 자식(`fields`)은 `children`으로 **보존**한다(떨구면 저작 표면이 그 자식을 영영
 * 모른다 — RepeatSourceField.children 주석).
 */
function catalogFieldToSource(f: CatalogField): RepeatSourceField {
  const nested = f.type === 'object' || f.type === 'array' || (f.fields?.length ?? 0) > 0;
  if (!nested) return { key: f.path, label: f.label };
  const children = declaredChildFields(f);
  return children.length
    ? { key: f.path, label: f.label, nested: true, children }
    : { key: f.path, label: f.label, nested: true };
}

/**
 * 템플릿 dataDefs + 카탈로그(cardinality='list' entry) +
 * 조립형 데이터 소스(dataSources, cardinality='list')를 RepeatSource[]로 통합한다.
 * 반복영역 모달의 소스 목록 + 필드 매핑 후보로 쓴다.
 * 순서: 내장 데이터셋 → 카탈로그 배열 → 조립형 데이터 소스.
 *
 * dataSources는 optional — 미전달이면 해당 소스는 나열되지 않는다(하위호환).
 *
 * **조립(compute) 내장 데이터셋의 칼럼은 파생한다** — 엔진이 base/source 행을 그대로 전달하므로
 * 입력 소스의 칼럼 + 조립 출력 키가 모두 유효 칼럼이다(deriveComputeDataDefFields).
 * 반복영역 매핑·셀 바인딩 콤보·병합 필드 소스 콤보가 전부 이 목록을 보므로, base 칼럼을 손으로
 * 반복 선언하지 않아도 전체 칼럼이 후보로 뜬다. 입력 해소를 위해 **2-pass**로 만든다
 * (1: 선언 칼럼으로 전 소스 나열 → 2: compute 보유 dataDef만 파생 칼럼으로 교체).
 */
export function collectRepeatSources(
  dataDefs: DataDef[] | undefined,
  catalog: CatalogEntry[],
  dataSources?: DataSourceDef[],
): RepeatSource[] {
  const sources: RepeatSource[] = [];

  for (const def of dataDefs ?? []) {
    sources.push({
      key: def.key,
      label: def.label,
      kind: 'dataDef',
      fields: def.fields.map((f) => ({ key: f.key, label: f.label })),
    });
  }

  for (const entry of catalog) {
    if (entry.cardinality !== 'list') continue;
    sources.push({
      key: entry.key,
      label: entry.label,
      kind: 'catalog',
      fields: entry.returns.map(catalogFieldToSource),
      // seedOnly는 소비 측에서만 filter.
      ...(entry.seedOnly ? { seedOnly: true as const } : {}),
    });
  }

  // 카탈로그 단건(single) entry의 배열 필드 — 예: "점검/현장 담당자 서명 › 점검 기여자(부) 서명 목록".
  // repeat 노드가 자체 binding(catalogKey=entryKey, select=fieldPath, bindingKey=key)으로 배열을
  // RenderData[key]에 적재해 over가 소비한다. key는 합성 관례(`entry.field`)와 동일한 점 표기.
  for (const entry of catalog) {
    if (entry.cardinality !== 'single') continue;
    for (const f of entry.returns) {
      if (f.type !== 'array' || !f.fields?.length) continue;
      sources.push({
        key: `${entry.key}.${f.path}`,
        label: `${entry.label} › ${f.label}`,
        kind: 'catalogField',
        entryKey: entry.key,
        fieldPath: f.path,
        fields: f.fields.map(catalogFieldToSource),
        ...(entry.seedOnly ? { seedOnly: true as const } : {}),
      });
    }
  }

  // 조립형 데이터 소스(dataSources) — cardinality='list' 선언만 행 데이터셋으로 나열(single=flat 소비).
  // 필드는 def.fields(조립 결과 필드)에서 유도(field=DataSourceFieldMap.field, 모두 평탄).
  for (const ds of dataSources ?? []) {
    if (ds.cardinality !== 'list') continue;
    sources.push({
      key: ds.key,
      label: ds.label,
      kind: 'dataSource',
      // widgetKey는 선언된 필드에만 실어 보낸다(계약 자식 파생의 근거 — RepeatSourceField 주석 참조).
      // 카탈로그 선언 자식(children)은 from을 catalog로 해소해 싣는다 — 있을 때만(라운드트립 노이즈 0).
      fields: ds.fields.map((f) => {
        const out: RepeatSourceField = { key: f.field, label: f.label ?? f.field };
        if (f.widgetKey) out.widgetKey = f.widgetKey;
        const children = declaredChildrenForDataSourceField(ds, f, catalog);
        if (children.length) out.children = children;
        return out;
      }),
    });
  }

  // 2-pass — compute 보유 내장 데이터셋의 칼럼을 파생 목록으로 교체한다.
  // (1-pass 결과 전체를 입력 해소에 쓰므로, 입력이 뒤에 선언된 소스여도 정상 해소된다.)
  for (const def of dataDefs ?? []) {
    if (!def.compute) continue;
    const target = sources.find((s) => s.kind === 'dataDef' && s.key === def.key);
    if (!target) continue;
    // origin/fromLabel(편집기 구획 표시용)은 소스 계약 밖이라 여기서 떨군다. nested·widgetKey·children은
    // 소비 측 계약이라 보존한다(상속 입력이 dataSource면 그 필드의 계약·선언 자식이 파생 대상이다).
    target.fields = deriveComputeDataDefFields(def, sources, dataDefs).fields.map((f) => {
      const out: RepeatSourceField = { key: f.key, label: f.label };
      if (f.nested) out.nested = true;
      if (f.widgetKey) out.widgetKey = f.widgetKey;
      if (f.children) out.children = f.children;
      return out;
    });
  }

  return sources;
}

/**
 * repeat 노드가 가리키는 소스를 역추적한다 — over 키 일치 우선, 없으면 binding(catalogKey+select)
 * 기반(직접 구성/구버전 노드는 over가 임의 키여도 binding이 같은 데이터를 가리키면 그 소스).
 * RepeatSourceEditor(소스 콤보 현재값)와 "반복 항목 필드" 콤보(항목 필드 후보)가 공유한다.
 */
export function findRepeatSource(
  sources: RepeatSource[],
  node: { over: string; binding?: { catalogKey?: string; select?: string } },
): RepeatSource | undefined {
  const byKey = sources.find((s) => s.key === node.over);
  if (byKey) return byKey;
  if (!node.binding?.catalogKey) return undefined;
  return (
    sources.find(
      (s) =>
        s.kind === 'catalogField' &&
        s.entryKey === node.binding?.catalogKey &&
        s.fieldPath === node.binding?.select,
    ) ?? sources.find((s) => s.kind === 'catalog' && s.key === node.binding?.catalogKey)
  );
}

/**
 * 밴드 위쪽(row<rowStart) 같은 칼럼의 가장 가까운 정적 텍스트 셀을 헤더 라벨로 유추한다.
 * "＋칼럼 추가" 픽 흐름이 새 칼럼 라벨을 유추할 때 사용한다.
 */
export function deriveHeaderLabel(
  table: TableNode,
  col: number,
  rowStart: number,
): string | undefined {
  let best: { row: number; text: string } | undefined;
  for (const c of table.cells) {
    if (c.col !== col) continue;
    if (c.row >= rowStart) continue;
    const text = typeof c.text === 'string' ? c.text.trim() : '';
    if (!text) continue;
    if (!best || c.row > best.row) best = { row: c.row, text };
  }
  return best?.text;
}
